import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PyramidEntityDto, SearchPyramidEntitiesRequest } from '../../../models/api';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { MaterialModule } from '../../../shared/material.module';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LargeShareholderService } from '../../../services/large-shareholder.service';
import { Helpers } from '../../../utilities/helpers';
import { EntityType } from '../../../models/enums/entityType.enum';
import { MatButtonModule } from '@angular/material/button';
import { AddPyramidItemComponent } from '../add-pyramid-item/add-pyramid-item.component';
import { TextNormalizeDirective } from '../../../directives/text-normalize.directive';

@Component({
  selector: 'app-add-pyramid-search-connect',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    MaterialModule,
    FormsModule,
    AddPyramidItemComponent,
    TextNormalizeDirective,
  ],
  templateUrl: './add-pyramid-search-connect.component.html',
  styleUrl: './add-pyramid-search-connect.component.scss'
})
export class AddPyramidSearchConnectComponent implements AfterViewInit {
  @Input() largeShareholderId!: string;
  @Input() dialogRef?: any;
  @Output() formValidityChanged = new EventEmitter<boolean>();
  @ViewChild(AddPyramidItemComponent) addPyramidItemComponent!: AddPyramidItemComponent;

  dataSource = new MatTableDataSource<PyramidEntityDto>([]);
  displayedColumns: string[] = ['actions', 'matchedYearsIcon', 'code', 'name', 'entityType'];
  pageSize: number = 5;
  showForm: boolean = false;
  connectedItemName?: string;
  connectedEntityId?: string;
  connectedEntityType?: 'largeShareholder' | 'pyramidalShareholder' | 'company' | 'opbCompany' | 'director';
  connectedCategoryId?: string;
  connectedSubCategoryId?: string;

  // Expose Helpers class to template
  protected readonly Helpers = Helpers;

  @ViewChild(MatSort, { static: false }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  searchForm = {
    name: undefined
  };

  constructor(
    private largeShareholderService: LargeShareholderService,
    private spinnerService: NgxSpinnerService,
    private router: Router
  ) {
    this.setupCustomSorting();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  private setupCustomSorting(): void {
    this.dataSource.sortingDataAccessor = (item: PyramidEntityDto, property: string) => {
      switch (property) {
        case 'entityType':
          return Helpers.getEntityTypeText(item.entityType);
        case 'name':
          return item.name;
        default:
          return (item as any)[property];
      }
    };
  }

  onSearch(): void {
    // Implement search logic here
    this.searchPyramidEntities();
  }

  searchPyramidEntities(): void {
    // Implement search logic here

    const request: SearchPyramidEntitiesRequest = {
      searchTerm: this.searchForm.name!
    };

    this.spinnerService.show();
    this.largeShareholderService.searchPyramidEntities(request).subscribe({
      next: (data) => {
        this.dataSource.data = data.map(r => {
          if (r.matchedYears?.length && (r.year == null || !r.matchedYears.includes(r.year))) {
            return { ...r, matchedYearsTooltip: `Matched in ${r.matchedYears.join(', ')}` };
          }
          return r;
        });
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error searching pyramid entities:', error);
        this.spinnerService.hide();
      }
    });
  }

  onConnect(item: PyramidEntityDto): void {
    this.connectedItemName = item.name;
    this.connectedEntityId = item.id;
    this.connectedCategoryId = item.categoryId;
    this.connectedSubCategoryId = item.subCategoryId;

    // Map entity type from the DTO to our component's type
    switch (item.entityType) {
      case 1: // LargeShareholder
        this.connectedEntityType = 'largeShareholder';
        break;
      case 2: // PyramidalShareholder
        this.connectedEntityType = 'pyramidalShareholder';
        break;
      case 3: // Company
        this.connectedEntityType = 'company';
        break;
      case 4: // OpbCompany
        this.connectedEntityType = 'opbCompany';
        break;
      case 5: // Director
        this.connectedEntityType = 'director';
        break;
    }

    this.showForm = true;
    this.formValidityChanged.emit(true);
  }

  onFormValidityChange(isValid: boolean) {
    this.formValidityChanged.emit(isValid);
  }

  /**
   * Same aid as the Ownership tab's shareholder search: directors are the results most often confused
   * with one another — same or similar names, several companies each — so their record can be opened
   * alongside the dialog to check the CV before connecting.
   */
  canOpenInNewTab(item: PyramidEntityDto): boolean {
    return item.entityType === EntityType.Director;
  }

  onOpenInNewTab(item: PyramidEntityDto, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canOpenInNewTab(item)) return;

    const url = this.router.serializeUrl(this.router.createUrlTree(['/directors/edit', item.id]));
    window.open(url, '_blank');
  }
}
